import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

export interface UnderlineTab<T extends string> {
  id: T;
  label: string;
  /** Shown after the label, smaller and grey (navy when selected). */
  count?: number;
}

/**
 * The platform's underline tabs (Health Concerns' status filter, Complete
 * History's Allergies / Conditions / …): plain text on a thin grey line; the
 * selected one turns navy and bold, lifts a little and grows a navy bar.
 * Scrolls sideways on narrow screens, never up and down.
 */
export function UnderlineTabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
  className,
  wrap = false,
}: {
  tabs: UnderlineTab<T>[];
  active: T;
  onChange: (id: T) => void;
  /** Accessible name of the tab list. */
  label: string;
  className?: string;
  /** In a narrow column (e.g. a sidebar): tabs that don't fit go to a second
   *  line instead of scrolling sideways, so the column never grows. */
  wrap?: boolean;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        "flex max-w-full items-end border-b border-[#E5E7EB]",
        wrap ? "flex-wrap gap-x-5 gap-y-1" : "msq-no-scrollbar gap-7 overflow-x-auto overflow-y-hidden",
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = active === tab.id;
        return (
          <Button
            key={tab.id}
            variant="ghost"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.id)}
            className="!relative !h-auto !shrink-0 !rounded-none !border-0 !bg-transparent !px-0 !pb-3 !pt-1 !shadow-none !outline-none hover:!bg-transparent focus-visible:!outline-none"
          >
            <span
              className={cn(
                "flex items-center gap-1.5 whitespace-nowrap text-sm transition-all duration-200 ease-out",
                selected
                  ? "-translate-y-0.5 scale-[1.03] font-bold text-[#180047]"
                  : "font-semibold text-[#6B7280] hover:text-[#180047]",
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={cn("text-xs font-semibold", selected ? "text-[#180047]" : "text-[#9CA3AF]")}>{tab.count}</span>
              )}
            </span>
            <span
              aria-hidden="true"
              className={cn(
                "absolute inset-x-0 bottom-0 h-[3px] origin-center rounded-full bg-[#180047] transition-transform duration-300 ease-out",
                selected ? "scale-x-100" : "scale-x-0",
              )}
            />
          </Button>
        );
      })}
    </div>
  );
}
